import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setDrawer } from "../../redux-store/features/layoutSlice";
import {
  Squares2X2Icon,
  ListBulletIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import PageToolbarFilters, {
  defaultGetOptionValue,
} from "../../components/Filters/PageToolbarFilters";
import ToolbarSearch from "../../components/Filters/ToolbarSearch";
import TableExportMenu from "../../components/Menus/TableExportMenu";
import AddToMyVendorsModal from "../../components/MyVendors/AddToMyVendorsModal";
import AddToListModal from "../../components/MyVendors/AddToListModal";
import CreateVendorListModal from "../../components/MyVendors/CreateVendorListModal";
import VendorListCardGrid from "../../components/MyVendors/VendorListCardGrid";
import MyVendorsVendorSections from "../../components/MyVendors/MyVendorsVendorSections";
import MyVendorsSelectionBar from "../../components/MyVendors/MyVendorsSelectionBar";
import MyVendorsLibraryDrawer from "../../components/MyVendors/MyVendorsLibraryDrawer";
import { resolveRequestAction, getRequestVqqCreatePath, getRequestVqaCreatePath } from "../../utils/myVendorsRequestUtils";
import { setSearchTextByPage } from "../../redux-store/features/providerFiltersSlice";
import { setVendorsFilters } from "../../redux-store/features/indicationsDashboardFiltersSlice";
import { canUseTrialExperienceDashboard } from "../../utils/orgFeatureGates";
import {
  useGetAllProviderTypes,
  UseGetRfiListForFilters,
} from "../../custom-hooks/queries";
import useMyVendors from "../../custom-hooks/useMyVendors";
import useMyVendorMembershipActions from "../../custom-hooks/useMyVendorMembershipActions";
import { broadcastMyVendorsChanged } from "../../utils/myVendorMembershipBroadcast";
import useMyVendorLists from "../../custom-hooks/useMyVendorLists";
import useMyVendorListActions from "../../custom-hooks/useMyVendorListActions";
import useBookmarkedVendors from "../../hooks/useBookmarkedVendors";
import { useDecodedToken } from "../../custom-hooks/useToken";
import useMyVendorsPageStickyFilters from "../../custom-hooks/useMyVendorsPageStickyFilters";
import { generateV2ServiceCategoryOptions } from "../../utils/serviceCategoryV2Mapping";
import useVendorTags from "../../hooks/useVendorTags";
import { applyMyVendorsFilters } from "../../utils/myVendorsFilters";
import { applyVendorListsFilters } from "../../utils/myVendorListsFilters";
import {
  MY_VENDORS_PAGE_KEY,
  MY_VENDORS_ROUTE,
  MY_VENDORS_TAB_LISTS,
  MY_VENDORS_TAB_MINE,
  buildMyVendorsFilterSections,
  buildVendorListsFilterSections,
  EMPTY_MY_VENDORS_SELECTED_IDS,
} from "../../utils/myVendorsFilterOptions";
import { vendorsFromListForCreate } from "../../utils/myVendorListsUtils";
import {
  buildMyVendorsPageExportData,
  buildVendorListsTabExportData,
} from "../../utils/exportVendorList";
import { myVendorsPageCellStyles } from "../../utils/exportBadgeStyles";
import { isSystemListId, SYSTEM_LIST_IDS } from "../../utils/myVendorListsConstants";
import { VENDOR_LIST_DETAIL_ROUTE } from "../../utils/myVendorListsConstants";

/** Preserves query string when redirecting legacy /MyVendors links. */
export function MyVendorsLegacyRouteRedirect() {
  const { search } = useLocation();
  return <Navigate to={`${MY_VENDORS_ROUTE}${search}`} replace />;
}

function parseTabFromSearch(search) {
  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  if (tab === "lists" || tab === "bookmarked") return MY_VENDORS_TAB_LISTS;
  if (tab === "mine") return MY_VENDORS_TAB_MINE;
  return MY_VENDORS_TAB_MINE;
}

export default function MyVendorsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState(() => parseTabFromSearch(location.search));
  const [viewMode, setViewMode] = useState("card");
  const [selectedIds, setSelectedIds] = useMyVendorsPageStickyFilters(activeTab);
  const [selectedVendorIds, setSelectedVendorIds] = useState(() => new Set());
  const [addVendorModalOpen, setAddVendorModalOpen] = useState(false);
  const [createListModalOpen, setCreateListModalOpen] = useState(false);
  const [addToListModalOpen, setAddToListModalOpen] = useState(false);
  const [newlyCreatedListId, setNewlyCreatedListId] = useState(null);
  const [libraryDrawerDocId, setLibraryDrawerDocId] = useState(null);
  const [pendingVendorActions, setPendingVendorActions] = useState({});
  const [addToListSubmitting, setAddToListSubmitting] = useState(false);

  const isDrawerOpen = useSelector((state) => state.layout.drawerOpen);
  const loggedInUser = useSelector((state) => state.loggedInUser);
  const { rights } = useSelector((state) => state.userRights);

  const canCompareTrialExperience = canUseTrialExperienceDashboard(loggedInUser, rights);

  const searchQuery =
    useSelector((state) => state.providersFilters.searchTextByPage[MY_VENDORS_PAGE_KEY]) ||
    "";

  const { decoded, error: tokenError } = useDecodedToken();
  const sponsorId = decoded?.sponsor_id ?? null;
  const tokenLoading = !decoded && !tokenError;

  const { data: providerTypes } = useGetAllProviderTypes();
  const { data: rfis } = UseGetRfiListForFilters();
  const { allKnownTags, vendorTagsMap, isError: tagsApiError } = useVendorTags(sponsorId);
  const {
    vendors: myVendors,
    providerIds,
    isLoading: vendorsLoading,
    isError: vendorsApiError,
    isEmpty: vendorsEmpty,
  } = useMyVendors(sponsorId, vendorTagsMap);
  const {
    bookmarkedProviderIds,
    isBookmarked,
    toggleBookmark,
    addBookmark,
    addBookmarks,
    isLoading: bookmarksLoading,
  } = useBookmarkedVendors(sponsorId);
  const {
    lists: vendorLists,
    userLists: userVendorLists,
    isLoading: vendorListsLoading,
    isError: vendorListsApiError,
  } = useMyVendorLists(sponsorId, { myVendors, bookmarkedProviderIds });

  const {
    createList,
    renameList,
    deleteList,
    addMembersToList,
    exportListAsCSV,
    exportListAsExcel,
    isMutating: listMutating,
  } = useMyVendorListActions(sponsorId, { myVendors, vendorTagsMap });

  const {
    handleAddToMyVendors: addSingleManualVendor,
    handleRemoveFromMyVendors,
    handleAddToHidden,
    handleUnhide,
    isMutating: membershipMutating,
  } = useMyVendorMembershipActions(sponsorId);

  const v2ServiceOptions = useMemo(
    () => generateV2ServiceCategoryOptions(rfis || []),
    [rfis]
  );

  // Legacy ?tab=bookmarked → ?tab=lists
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tab") === "bookmarked") {
      params.set("tab", "lists");
      navigate(
        { pathname: MY_VENDORS_ROUTE, search: params.toString() },
        { replace: true }
      );
    }
  }, [location.search, navigate]);

  useEffect(() => {
    setActiveTab(parseTabFromSearch(location.search));
  }, [location.search]);

  // Clear row selection when switching tabs (filters restore per-tab via sticky key).
  useEffect(() => {
    setSelectedVendorIds(new Set());
  }, [activeTab]);

  const syncTabToUrl = useCallback(
    (tab) => {
      const params = new URLSearchParams(location.search);
      if (tab === MY_VENDORS_TAB_MINE) {
        params.delete("tab");
      } else {
        params.set("tab", "lists");
      }
      const search = params.toString();
      navigate(
        { pathname: MY_VENDORS_ROUTE, search: search ? `?${search}` : "" },
        { replace: false }
      );
    },
    [location.search, navigate]
  );

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    syncTabToUrl(tab);
  };

  const hasAnyBookmarkedInMyVendors = useMemo(
    () => myVendors.some((vendor) => isBookmarked(vendor?.id)),
    [myVendors, isBookmarked]
  );

  const filterSections = useMemo(() => {
    if (activeTab === MY_VENDORS_TAB_LISTS) {
      return buildVendorListsFilterSections({
        allKnownTags,
        v2ServiceOptions,
        vendorLists,
        fallbackVendors: myVendors,
      });
    }
    return buildMyVendorsFilterSections({
      providerTypes: providerTypes || [],
      v2ServiceOptions,
      allKnownTags,
      includeBookmarked: hasAnyBookmarkedInMyVendors,
    });
  }, [
    activeTab,
    providerTypes,
    v2ServiceOptions,
    allKnownTags,
    myVendors,
    vendorLists,
    hasAnyBookmarkedInMyVendors,
  ]);

  const visibleFilterSections = useMemo(
    () => filterSections.filter((s) => (s.options?.length ?? 0) > 0),
    [filterSections]
  );

  const hasActiveFilters = useMemo(
    () => Object.values(selectedIds).some((arr) => Array.isArray(arr) && arr.length > 0),
    [selectedIds]
  );

  const handleClearAllFilters = () => {
    setSelectedIds({ ...EMPTY_MY_VENDORS_SELECTED_IDS });
  };

  // Drop the Bookmarked filter only after we know My Vendors + bookmarks have
  // finished loading for this sponsor. Disabled queries (no sponsorId yet) report
  // isLoading=false with empty data — clearing then would wipe sticky bookmarked.
  const canReconcileBookmarkedFilter =
    Boolean(sponsorId) &&
    !tokenLoading &&
    !vendorsLoading &&
    !bookmarksLoading;

  useEffect(() => {
    if (!canReconcileBookmarkedFilter || hasAnyBookmarkedInMyVendors) {
      return;
    }
    setSelectedIds((prev) => {
      if (!prev.bookmarked?.length) return prev;
      return { ...prev, bookmarked: [] };
    });
  }, [canReconcileBookmarkedFilter, hasAnyBookmarkedInMyVendors]);

  const filteredVendors = useMemo(
    () =>
      applyMyVendorsFilters(myVendors, {
        searchQuery,
        selectedIds,
        providerTypes: providerTypes || [],
        isBookmarked,
        rfis: rfis || [],
      }),
    [myVendors, searchQuery, selectedIds, providerTypes, isBookmarked, rfis]
  );

  const filteredVendorLists = useMemo(
    () =>
      applyVendorListsFilters(vendorLists, {
        searchQuery,
        selectedIds,
        rfis: rfis || [],
        vendorTagsMap,
      }),
    [vendorLists, searchQuery, selectedIds, rfis, vendorTagsMap]
  );

  const showVendorsLoading = tokenLoading || vendorsLoading;
  const showVendorListsLoading = tokenLoading || vendorListsLoading;
  const myVendorsTabCount = showVendorsLoading ? "…" : filteredVendors.length;
  const vendorListsTabCount = showVendorListsLoading ? "…" : vendorLists.length;
  const vendorListsHasActiveQueryOrFilters =
    Boolean(searchQuery.trim()) || hasActiveFilters;

  const selectedVendors = useMemo(
    () => myVendors.filter((vendor) => selectedVendorIds.has(String(vendor.id))),
    [myVendors, selectedVendorIds]
  );

  const allFilteredVendorsSelected = useMemo(() => {
    if (!filteredVendors.length) return false;
    return filteredVendors.every((vendor) => selectedVendorIds.has(String(vendor.id)));
  }, [filteredVendors, selectedVendorIds]);

  const someFilteredVendorsSelected = useMemo(() => {
    if (!filteredVendors.length) return false;
    return filteredVendors.some((vendor) => selectedVendorIds.has(String(vendor.id)));
  }, [filteredVendors, selectedVendorIds]);

  const handleSelectAllFilteredVendors = useCallback(() => {
    if (allFilteredVendorsSelected) {
      setSelectedVendorIds((prev) => {
        const next = new Set(prev);
        filteredVendors.forEach((vendor) => next.delete(String(vendor.id)));
        return next;
      });
      return;
    }

    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      filteredVendors.forEach((vendor) => next.add(String(vendor.id)));
      return next;
    });
  }, [allFilteredVendorsSelected, filteredVendors]);

  const handleRemoveSelectedVendor = useCallback((vendor) => {
    const id = String(vendor?.id || "");
    if (!id) return;
    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleCompareSelectedVendors = useCallback(() => {
    if (!selectedVendors.length) return;
    dispatch(
      setVendorsFilters({
        providerIds: selectedVendors.map((vendor) => vendor.id).filter(Boolean),
      })
    );
    navigate("/TrialExperience");
  }, [dispatch, navigate, selectedVendors]);

  const handleCreateListFromSelection = useCallback(() => {
    setCreateListModalOpen(true);
  }, []);

  const setVendorActionPending = useCallback((vendorId, action, pending) => {
    const key = `${String(vendorId)}:${action}`;
    setPendingVendorActions((prev) => {
      if (pending) return { ...prev, [key]: true };
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const isVendorActionPending = useCallback(
    (vendorId, action) => Boolean(pendingVendorActions[`${String(vendorId)}:${action}`]),
    [pendingVendorActions]
  );

  const handleAddToListFromSelection = useCallback(() => {
    setAddToListModalOpen(true);
  }, []);

  const handleAddVendorsToList = useCallback(
    async (list, vendors) => {
      setAddToListSubmitting(true);
      try {
        if (isSystemListId(list.id)) {
          if (list.id === SYSTEM_LIST_IDS.BOOKMARKED) {
            await addBookmarks(vendors.map((vendor) => vendor.id));
            return;
          }
          if (list.id === SYSTEM_LIST_IDS.MY_VENDORS) {
            await Promise.all(
              vendors.map((vendor) => addSingleManualVendor(vendor, { skipBroadcast: true }))
            );
            broadcastMyVendorsChanged(sponsorId);
          }
          return;
        }
        await addMembersToList(list.id, vendors);
      } finally {
        setAddToListSubmitting(false);
      }
    },
    [addBookmarks, addMembersToList, addSingleManualVendor, sponsorId]
  );

  const handleOpenVendorList = useCallback(
    (list) => {
      if (!list?.id) return;
      navigate(`${VENDOR_LIST_DETAIL_ROUTE}/${list.id}`);
    },
    [navigate]
  );

  const handleCreateVendorList = useCallback(
    async (payload) => {
      await createList(payload);
    },
    [createList]
  );

  const handleRenameVendorList = useCallback(
    async (list, newName) => {
      await renameList(list.id, newName);
    },
    [renameList]
  );

  const handleDuplicateVendorList = useCallback(
    async (list) => {
      const copy = await createList({
        name: `${list.name} (Copy)`,
        notes: list.notes || "",
        vendors: vendorsFromListForCreate(list),
      });
      if (copy?.id) {
        setNewlyCreatedListId(copy.id);
      }
    },
    [createList]
  );

  const handleDeleteVendorList = useCallback(
    async (list) => {
      await deleteList(list.id);
    },
    [deleteList]
  );

  const handleAddVendor = () => {
    setAddVendorModalOpen(true);
  };

  const handleAddVendorsFromModal = async (providerIdsToAdd) => {
    await Promise.all(
      providerIdsToAdd.map((providerId) =>
        addSingleManualVendor({ id: providerId }, { skipBroadcast: true })
      )
    );
    broadcastMyVendorsChanged(sponsorId);
  };

  useEffect(() => {
    if (!isDrawerOpen) {
      setLibraryDrawerDocId(null);
    }
  }, [isDrawerOpen]);

  const handleRequestAction = useCallback(
    (request) => {
      const action = resolveRequestAction(request);
      if (!action) return;
      if (action.kind === "drawer") {
        setLibraryDrawerDocId(action.documentId);
        dispatch(setDrawer(true));
        return;
      }
      if (action.kind === "navigate" && action.path) {
        navigate(action.path);
      }
    },
    [dispatch, navigate]
  );

  const handleToggleVendorSelection = useCallback((vendor) => {
    const id = String(vendor?.id || "");
    if (!id) return;
    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleVendorAction = useCallback(
    async (actionId, vendor) => {
      const providerId = vendor?.id;
      if (!providerId) return;

      const pendingKey =
        actionId === "toggle-bookmark"
          ? "bookmark"
          : actionId === "add-to-hidden"
            ? "hide"
            : actionId === "unhide" || actionId === "remove-from-hidden"
              ? "unhide"
              : null;

      try {
        if (pendingKey) {
          setVendorActionPending(providerId, pendingKey, true);
        }

        switch (actionId) {
          case "view-profile":
            navigate(`/provider/${providerId}/overview`);
            break;
          case "request-vqq": {
            const path = getRequestVqqCreatePath(providerId);
            if (path) navigate(path);
            break;
          }
          case "request-vqa": {
            const path = getRequestVqaCreatePath(providerId);
            if (path) navigate(path);
            break;
          }
          case "remove-from-my-vendors":
            await handleRemoveFromMyVendors(vendor);
            setSelectedVendorIds((prev) => {
              const next = new Set(prev);
              next.delete(String(providerId));
              return next;
            });
            break;
          case "add-to-hidden":
            await handleAddToHidden(vendor);
            break;
          case "unhide":
          case "remove-from-hidden":
            await handleUnhide(vendor);
            break;
          case "toggle-bookmark":
            await toggleBookmark(providerId);
            break;
          default:
            break;
        }
      } catch (error) {
        console.error(`My Vendors action "${actionId}" failed:`, error);
      } finally {
        if (pendingKey) {
          setVendorActionPending(providerId, pendingKey, false);
        }
      }
    },
    [
      handleAddToHidden,
      handleRemoveFromMyVendors,
      handleUnhide,
      navigate,
      setVendorActionPending,
      toggleBookmark,
    ]
  );

  const searchPlaceholder =
    activeTab === MY_VENDORS_TAB_LISTS
      ? "Search vendor lists"
      : "Search my vendors";

  return (
    <div className="min-h-screen w-full bg-gray-50 px-4 pb-6 sm:px-6 lg:px-8">
      <div className="py-8">
        <nav className="mb-4 flex" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <button
                type="button"
                onClick={() => navigate("/Providers")}
                className="text-sm text-gray-500 transition-colors hover:text-indigo-600"
              >
                Search Vendors
              </button>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="mx-1 h-3 w-3 text-gray-400"
                  aria-hidden="true"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-900">My Vendors</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">My Vendors</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your requested vendors and vendor lists
          </p>
        </div>

        <div className="relative z-30 mb-6 w-full min-w-0 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex w-full min-w-0 flex-wrap items-end justify-between gap-4">
            <div className="w-full min-w-0 flex-1 sm:max-w-xl">
              <ToolbarSearch
                value={searchQuery}
                onDebouncedChange={(text) =>
                  dispatch(
                    setSearchTextByPage({ pageKey: MY_VENDORS_PAGE_KEY, searchText: text })
                  )
                }
                placeholder={searchPlaceholder}
                inputClassName="w-[300px] max-w-full"
              />
            </div>
            {visibleFilterSections.length > 0 && (
              <div className="flex w-full min-w-0 flex-wrap items-center gap-3 lg:w-auto lg:flex-shrink-0">
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={handleClearAllFilters}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    Clear all
                  </button>
                )}
                <PageToolbarFilters
                  sections={visibleFilterSections}
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                  getOptionValue={defaultGetOptionValue}
                  serviceCategoryV2SourceData={rfis || []}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mb-6 w-full min-w-0 border-b border-gray-200">
          <div className="flex w-full min-w-0 items-center justify-between">
            <nav className="-mb-px flex space-x-8" aria-label="My Vendors tabs">
              <button
                type="button"
                onClick={() => handleTabChange(MY_VENDORS_TAB_MINE)}
                className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                  activeTab === MY_VENDORS_TAB_MINE
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                My Vendors
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    activeTab === MY_VENDORS_TAB_MINE
                      ? "bg-indigo-100 text-indigo-600"
                      : "bg-gray-100 text-gray-900"
                  }`}
                >
                  {myVendorsTabCount}
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleTabChange(MY_VENDORS_TAB_LISTS)}
                className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                  activeTab === MY_VENDORS_TAB_LISTS
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                Vendor Lists
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    activeTab === MY_VENDORS_TAB_LISTS
                      ? "bg-indigo-100 text-indigo-600"
                      : "bg-gray-100 text-gray-900"
                  }`}
                >
                  {vendorListsTabCount}
                </span>
              </button>
            </nav>

            <div className="flex items-center gap-2">
              {activeTab === MY_VENDORS_TAB_MINE && (
                <button
                  type="button"
                  onClick={handleAddVendor}
                  className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  <UserPlusIcon className="h-4 w-4" aria-hidden="true" />
                  Add Vendor
                </button>
              )}

              {activeTab === MY_VENDORS_TAB_LISTS && (
                <button
                  type="button"
                  onClick={() => setCreateListModalOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  Create List
                </button>
              )}

              {activeTab === MY_VENDORS_TAB_MINE && (
                <>
                  {viewMode === "list" ? (
                    <button
                      type="button"
                      className="rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-indigo-600"
                      onClick={() => setViewMode("card")}
                      aria-label="Grid view"
                      title="Grid view"
                    >
                      <Squares2X2Icon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-indigo-600"
                      onClick={() => setViewMode("list")}
                      aria-label="List view"
                      title="List view"
                    >
                      <ListBulletIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  )}
                </>
              )}

              <TableExportMenu
                getData={() =>
                  activeTab === MY_VENDORS_TAB_MINE
                    ? buildMyVendorsPageExportData(filteredVendors)
                    : buildVendorListsTabExportData(filteredVendorLists)
                }
                reportTitle={
                  activeTab === MY_VENDORS_TAB_LISTS ? "Vendor Lists" : "My Vendors"
                }
                reportSubtitle={
                  activeTab === MY_VENDORS_TAB_LISTS
                    ? "Custom vendor lists"
                    : "Requested vendors"
                }
                filePrefix={
                  activeTab === MY_VENDORS_TAB_LISTS ? "Vendor_Lists" : "My_Vendors"
                }
                downloadType="My Vendors"
                cellStyles={
                  activeTab === MY_VENDORS_TAB_MINE ? myVendorsPageCellStyles : null
                }
              />
            </div>
          </div>
        </div>

        {tagsApiError && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Custom tags are temporarily unavailable. Your vendor list still loads; tag editing and
            tag filters will return when the tags API is reachable.
          </div>
        )}

        {vendorsApiError && activeTab === MY_VENDORS_TAB_MINE && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            We could not load your vendors. Refresh the page or try again in a few minutes.
          </div>
        )}

        {vendorListsApiError && activeTab === MY_VENDORS_TAB_LISTS && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            We could not load your custom vendor lists. The default My Vendors and Bookmarked
            lists still appear below; try refreshing once the lists API is available.
          </div>
        )}

        {activeTab === MY_VENDORS_TAB_MINE ? (
          showVendorsLoading ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
              <p className="text-sm text-gray-500">Loading your vendors…</p>
            </div>
          ) : vendorsEmpty ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
              <p className="text-sm font-medium text-gray-900">No vendors linked to your organization yet.</p>
              <p className="mt-2 text-sm text-gray-500">
                Use Add Vendor to link vendors, or request vendors from Search Vendors.
              </p>
            </div>
          ) : (
            <>
              <MyVendorsSelectionBar
                selectedVendors={selectedVendors}
                allSelected={allFilteredVendorsSelected}
                onSelectAll={handleSelectAllFilteredVendors}
                onRemoveVendor={handleRemoveSelectedVendor}
                onCreateList={handleCreateListFromSelection}
                onAddToList={handleAddToListFromSelection}
                onCompare={handleCompareSelectedVendors}
                showCompare={canCompareTrialExperience}
              />
              <MyVendorsVendorSections
                vendors={filteredVendors}
                viewMode={viewMode}
                sponsorId={sponsorId}
                onVendorAction={handleVendorAction}
                onRequestAction={handleRequestAction}
                selectedVendorIds={selectedVendorIds}
                onToggleVendorSelection={handleToggleVendorSelection}
                allFilteredSelected={allFilteredVendorsSelected}
                someFilteredSelected={someFilteredVendorsSelected}
                onSelectAllFiltered={handleSelectAllFilteredVendors}
                isBookmarked={isBookmarked}
                isVendorActionPending={isVendorActionPending}
              />
            </>
          )
        ) : showVendorListsLoading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
            <p className="text-sm text-gray-500">Loading vendor lists…</p>
          </div>
        ) : filteredVendorLists.length === 0 && vendorListsHasActiveQueryOrFilters ? (
          <div className="w-full min-w-0 rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
            <p className="text-sm font-medium text-gray-900">No matching vendor lists</p>
            <p className="mt-2 text-sm text-gray-500">
              Try adjusting your search or clearing the active filters.
            </p>
          </div>
        ) : (
          <VendorListCardGrid
            lists={filteredVendorLists}
            newlyCreatedListId={newlyCreatedListId}
            onEditModeEntered={() => setNewlyCreatedListId(null)}
            onOpenList={handleOpenVendorList}
            onRenameList={handleRenameVendorList}
            onDuplicateList={handleDuplicateVendorList}
            onDeleteList={handleDeleteVendorList}
            onExportCsv={exportListAsCSV}
            onExportExcel={exportListAsExcel}
          />
        )}
      </div>

      <AddToMyVendorsModal
        isOpen={addVendorModalOpen}
        onClose={() => setAddVendorModalOpen(false)}
        excludeProviderIds={providerIds}
        onAddVendors={handleAddVendorsFromModal}
        isSubmitting={membershipMutating}
      />

      <CreateVendorListModal
        isOpen={createListModalOpen}
        onClose={() => setCreateListModalOpen(false)}
        onCreateList={handleCreateVendorList}
        existingListNames={userVendorLists.map((list) => list.name)}
        initialVendors={selectedVendors}
        isSubmitting={listMutating}
      />

      <AddToListModal
        isOpen={addToListModalOpen}
        onClose={() => setAddToListModalOpen(false)}
        selectedVendors={selectedVendors}
        vendorLists={vendorLists}
        onAddToList={handleAddVendorsToList}
        isSubmitting={addToListSubmitting}
      />

      <MyVendorsLibraryDrawer documentId={libraryDrawerDocId} />
    </div>
  );
}
